import * as React from 'react'

import { Box, Button, Section, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import Loading from 'modules/core/components/Loading'
import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import AddEditorModal from 'modules/enps/components/AddEditorModal'
import useFormAccessGroup from 'modules/enps/hooks/useAccessGroup'
import type Form from 'modules/enps/store/models/Form'
import EmployeeLabel from 'modules/surveys/components/Participants/common/EmployeeLabel'
import formAccessGroupMemberships from 'modules/surveys/store/collections/formAccessGroupMemberships'
import type FormAccessGroupMembership from 'modules/surveys/store/models/FormAccessGroupMembership'

interface ItemProps {
  membership: FormAccessGroupMembership
  form: Form
}

function Item({ membership, form }: ItemProps) {
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const [open, toggle] = useToggle()
  const editor = users.find({
    id: membership.attributes.access_id,
  })
  const editorId = editor?.attributes.id
  const editorIsCurrentAccess = editorId === currentAccessId
  const authorId = form.attributes.access_id
  const editorIsAuthor = editorId === authorId

  if (!editor) return null

  const title = i18n.t('enps.form.editors.remove_editor')

  // The current access can not delete themselves and can not the delete the author of the eNPS survey form
  const showDeleteButton = !editorIsCurrentAccess && !editorIsAuthor

  return (
    <Box
      key={editorId}
      alignItems="center"
      flexDirection="row"
      justifyContent="spaceBetween"
      paddingX="s24"
      paddingY="s32"
      width="full"
      flexGrow
    >
      <EmployeeLabel user={editor} />
      {showDeleteButton && (
        <Popover
          attachment="top right"
          targetAttachment="bottom center"
          toggle={toggle}
          open={open}
        >
          <Button
            onClick={toggle(true)}
            type="secondary"
            aria-label="remove editor"
            iconName="delete"
          />
          <PopoverContent
            onClose={toggle(false)}
            options={[
              {
                title: title,
                type: 'wrong',
                onClick: () =>
                  asyncAction(() => membership.destroy({ data: { form_id: form.attributes.id } })),
              },
            ]}
          />
        </Popover>
      )}
    </Box>
  )
}

interface ItemsProps {
  form: Form
}

const Items = observer(({ form }: ItemsProps) => {
  const memberships = formAccessGroupMemberships.toArray()

  if (!memberships.length) {
    return (
      <Box aria-label="List of editors">
        <BoxList>
          <Text.Normal>{i18n.t('surveys.form.managers.blank_slate')}</Text.Normal>
        </BoxList>
      </Box>
    )
  }

  return (
    <Box aria-label="List of editors">
      <BoxList>
        {memberships.map((membership) => (
          <Item key={membership.attributes.id} membership={membership} form={form} />
        ))}
      </BoxList>
    </Box>
  )
})

interface Props {
  form: Form
  contractVersions: ContractVersions
}

function Editors({ form, contractVersions }: Props) {
  const { loading } = useFormAccessGroup(form)
  const [openedModal, setOpenedModal] = React.useState(false)

  return (
    <Section
      illustration="people-add"
      title={i18n.t('enps.form.editors.title')}
      description={i18n.t('enps.form.editors.description')}
      action={{
        label: i18n.t('enps.form.editors.add_editor'),
        onClick: () => setOpenedModal(true),
      }}
    >
      {loading && <Loading />}
      {!loading && openedModal && (
        <AddEditorModal
          onClose={() => setOpenedModal(false)}
          form={form}
          contractVersions={contractVersions}
        />
      )}
      {!loading && <Items form={form} />}
    </Section>
  )
}

export default observer(Editors)
